import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { HelmetProvider } from "react-helmet-async";

import DashboardPage from "./pages/DashboardPage";
import JobsPage from "./pages/JobsPage";
import TradesPage from "./pages/TradesPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import OnboardingPage from "./pages/OnboardingPage";
import TradeQuoteResponsePage from "./pages/TradeQuoteResponsePage";
import TradesPurple from "./tradespurple";
import NotFound from "./pages/NotFound";

// TradesPurple pages
import TradesPurpleLoginPage from "./tradespurple/pages/LoginPage";
import TradesPurpleSignupPage from "./tradespurple/pages/SignupPage";
import TradesPurpleOnboardingPage from "./tradespurple/pages/OnboardingPage";

// Create a safe area CSS variable setup
const setIOSSafeAreaVariables = () => {
  // Only run in browser environment
  if (typeof document !== "undefined") {
    const style = document.createElement("style");
    style.innerHTML = `
      :root {
        --sat: env(safe-area-inset-top);
        --sar: env(safe-area-inset-right);
        --sab: env(safe-area-inset-bottom);
        --sal: env(safe-area-inset-left);
      }
      
      .pt-safe {
        padding-top: var(--sat);
      }
      
      .pr-safe {
        padding-right: var(--sar);
      }
      
      .pb-safe {
        padding-bottom: var(--sab, 20px);
      }
      
      .pl-safe {
        padding-left: var(--sal);
      }
    `;
    document.head.appendChild(style);
  }
};

// Initialize the safe area variables
setIOSSafeAreaVariables();

const queryClient = new QueryClient();

// Detect if we're running in TradesPurple mode based on the Vite build configuration
const isTradesPurpleMode = 
  typeof __APP_MODE__ !== 'undefined' && 
  __APP_MODE__ === 'tradespurple';

const App = () => {
  // In TradesPurple mode, only render TradesPurple routes
  if (isTradesPurpleMode) {
    return (
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <HelmetProvider>
            <BrowserRouter>
              <AuthProvider>
                <Routes>
                  {/* TradesPurple routes - auth handling is now inside TradesPurpleRoutes */}
                  <Route path="/*" element={<TradesPurple />} />
                  
                  {/* Public quote response route (no auth required) */}
                  <Route path="/trades/quote/:token" element={<TradeQuoteResponsePage />} />
                  
                  {/* 404 route */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </AuthProvider>
            </BrowserRouter>
            <Toaster />
            <Sonner />
          </HelmetProvider>
        </TooltipProvider>
      </QueryClientProvider>
    );
  }
  
  // Standard HouseHeld app routing
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <HelmetProvider>
          <BrowserRouter>
            <AuthProvider>
              <Routes>
                {/* Public routes (no auth required) */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/trades/quote/:token" element={<TradeQuoteResponsePage />} />
                <Route path="/tradespurple/*" element={<TradesPurple />} />
                
                {/* Auth-protected routes */}
                <Route path="/onboarding" element={<OnboardingPage />} />
                <Route path="/" element={<DashboardPage />} />
                <Route path="/jobs" element={<JobsPage />} />
                <Route path="/trades" element={<TradesPage />} />
                
                {/* 404 route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </AuthProvider>
          </BrowserRouter>
          <Toaster />
          <Sonner />
        </HelmetProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
